import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { notificationService, UserNotification } from '../utils/notificationService';
import { pollingNotificationService } from '../services/pollingNotificationService';
import { createLogger } from '../utils/logger';
import { useNotificationAnalytics } from '../hooks/useNotificationAnalytics';

const logger = createLogger({ module: 'NotificationContext' });

interface NotificationContextType {
  // Bell notifications
  bellNotifications: UserNotification[];
  unreadCount: number;
  
  // Banner notifications
  bannerNotifications: UserNotification[];
  activeBanner: UserNotification | null;
  
  // Page-specific notifications
  getUnreadCountForPage: (page: string) => number;
  getTotalUnreadCount: () => number; // Combined bell + banner count for mobile
  
  // Page-based auto-reading
  autoMarkBannersAsReadForPage: (page: string) => Promise<void>;
  getBannerNotificationsForPage: (page: string) => UserNotification[];
  
  // Actions
  markAsRead: (notificationId: string) => Promise<void>;
  dismissNotification: (notificationId: string) => Promise<boolean>;
  markAllAsRead: () => Promise<void>;
  refreshNotifications: () => Promise<void>;
  
  // Loading states
  isLoading: boolean;
  error: string | null;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  logger.debug('Notification provider rendering');
  
  const { user } = useAuth();
  const [bellNotifications, setBellNotifications] = useState<UserNotification[]>([]);
  const [bannerNotifications, setBannerNotifications] = useState<UserNotification[]>([]);
  const [activeBanner, setActiveBanner] = useState<UserNotification | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const cleanupRef = useRef<(() => void) | null>(null);
  const subscriptionRef = useRef<boolean>(false); // Track subscription state
  
  // Analytics tracking - use stable reference
  const analyticsRef = useRef(useNotificationAnalytics());
  const analytics = analyticsRef.current;
  
  logger.debug('Notification provider state', { 
    hasUser: !!user, 
    userId: user?.id,
    userEmail: user?.email,
    bellCount: bellNotifications.length,
    bannerCount: bannerNotifications.length
  });

  // Log when user changes
  useEffect(() => {
    logger.debug('User state changed', {
      hasUser: !!user,
      userId: user?.id,
      userEmail: user?.email,
      timestamp: new Date().toISOString()
    });
  }, [user]);

  const refreshNotifications = useCallback(async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch both notification types in parallel
      // Note: Don't include dismissed notifications since they are moved to history table by the trigger
      const [bellData, bannerData] = await Promise.all([
        notificationService.getUserNotifications({
          userId: user.id,
          deliveryMethod: 'bell',
          includeRead: true,
          includeDismissed: false, // Dismissed notifications are in history table
          limit: 50
        }),
        notificationService.getUserNotifications({
          userId: user.id,
          deliveryMethod: 'banner',
          includeRead: true,
          includeDismissed: false, // Dismissed notifications are in history table
          limit: 50
        })
      ]);
      
      setBellNotifications(bellData);
      setBannerNotifications(bannerData);
      
      // Set active banner to the highest priority unread banner
      const activeBannerCandidate = bannerData
        .filter(n => !n.read_at && !n.dismissed_at)
        .sort((a, b) => b.priority - a.priority)[0] || null;
      
      setActiveBanner(activeBannerCandidate);
      
    } catch (err) {
      logger.error('Error refreshing notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Memoized notification handler to prevent re-renders
  const handleRealtimeNotification = useCallback((notification: UserNotification & { _deleted?: boolean }) => {
    logger.debug('Realtime event received:', {
      id: notification.id,
      title: notification.title,
      delivery_method: notification.delivery_method,
      _deleted: notification._deleted,
      timestamp: new Date().toISOString()
    });
    
    // Handle delete events
    if (notification._deleted) {
      logger.debug('Removing deleted notification:', {
        id: notification.id,
        delivery_method: notification.delivery_method
      });
      
      // Remove from both bell and banner notifications regardless of delivery_method
      // since we don't know if the delivery_method is properly set in the DELETE event
      setBellNotifications(prev => {
        const filtered = prev.filter(n => n.id !== notification.id);
        if (filtered.length !== prev.length) {
          logger.debug('Removed notification from bell notifications', { id: notification.id });
        }
        return filtered;
      });
      
      setBannerNotifications(prev => {
        const filtered = prev.filter(n => n.id !== notification.id);
        if (filtered.length !== prev.length) {
          logger.debug('Removed notification from banner notifications', { id: notification.id });
        }
        return filtered;
      });
      
      // Remove active banner if it's the one being deleted
      setActiveBanner(current => {
        if (current?.id === notification.id) {
          logger.debug('Removed active banner', { id: notification.id });
          return null;
        }
        return current;
      });
      
      return;
    }
    
    // Handle insert events (new notifications)
    if (notification.delivery_method === 'bell') {
      logger.debug('Adding new bell notification', { 
        id: notification.id, 
        title: notification.title,
        read_at: notification.read_at,
        user_id: notification.user_id
      });
      setBellNotifications(prev => {
        // Check if notification already exists to prevent duplicates
        if (prev.some(n => n.id === notification.id)) {
          logger.debug('Notification already exists, skipping', { id: notification.id });
          return prev;
        }
        
        const newState = [notification, ...prev];
        const newUnreadCount = newState.filter(n => !n.read_at).length;
        logger.debug('Bell notifications state updated', { 
          previousCount: prev.length, 
          newCount: newState.length,
          previousUnread: prev.filter(n => !n.read_at).length,
          newUnreadCount: newUnreadCount
        });
        
        // Defer analytics tracking to avoid blocking the UI
        setTimeout(() => {
          analyticsRef.current.trackNotificationDelivered({
            notification_id: notification.id,
            notification_type: notification.notification_type,
            delivery_method: 'bell',
            category: notification.category,
            template_key: notification.template?.key,
            priority: notification.priority,
            user_has_unread: newUnreadCount > 0,
            total_unread_count: newUnreadCount
          });
        }, 0);
        
        return newState;
      });
    } else if (notification.delivery_method === 'banner') {
      logger.debug('Adding new banner notification to state', { id: notification.id });
      setBannerNotifications(prev => {
        // Check if notification already exists to prevent duplicates
        if (prev.some(n => n.id === notification.id)) {
          logger.debug('BANNER NOTIFICATION ALREADY EXISTS, SKIPPING', { id: notification.id });
          return prev;
        }
        
        const newState = [notification, ...prev];
        const newUnreadCount = newState.filter(n => !n.read_at).length;
        
        // Defer analytics tracking to avoid blocking the UI
        setTimeout(() => {
          analyticsRef.current.trackNotificationDelivered({
            notification_id: notification.id,
            notification_type: notification.notification_type,
            delivery_method: 'banner',
            category: notification.category,
            template_key: notification.template?.key,
            priority: notification.priority,
            target_page: notification.metadata?.target_page,
            user_has_unread: newUnreadCount > 0,
            total_unread_count: newUnreadCount
          });
        }, 0);
        
        return newState;
      });
      // Set as active banner if no current active banner
      setActiveBanner(current => current || notification);
    } else {
      logger.warn('Received notification with unknown delivery_method', { 
        id: notification.id, 
        delivery_method: notification.delivery_method 
      });
    }
  }, []); // Empty dependencies since we use refs

  // Initialize polling notification service - this effect should run only when user.id changes
  useEffect(() => {
    if (user?.id && !subscriptionRef.current) {
      logger.debug('Initializing polling notification service', { 
        userId: user.id, 
        userEmail: user.email,
        timestamp: new Date().toISOString(),
        wasSubscribed: subscriptionRef.current
      });
      
      // Mark as subscribed immediately to prevent double subscription
      subscriptionRef.current = true;
      
      // Clean up any existing subscription first
      if (cleanupRef.current) {
        logger.debug('Cleaning up existing polling subscription before reinit');
        cleanupRef.current();
        cleanupRef.current = null;
      }
      
      // Setup async subscription
      const setupSubscription = async () => {
        try {
          logger.debug('Subscribing to polling notifications', { userId: user.id });
          
          // Subscribe to notifications
          await pollingNotificationService.subscribe(user.id);
          
          logger.debug('Adding polling listener', { userId: user.id });
          
          // Add listener for notification changes
          cleanupRef.current = pollingNotificationService.addListener(user.id, handleRealtimeNotification);
          
          logger.debug('Performing initial load', { userId: user.id });
          // Initial load
          await refreshNotifications();
        } catch (error) {
          logger.error('Failed to setup polling subscription', { error, userId: user.id });
          // Reset subscription state on error
          subscriptionRef.current = false;
        }
      };
      
      setupSubscription();

      return () => {
        logger.debug('Cleaning up polling service', { userId: user.id });
        if (cleanupRef.current) {
          cleanupRef.current();
          cleanupRef.current = null;
        }
        pollingNotificationService.unsubscribe(user.id);
        // Reset subscription state on cleanup
        subscriptionRef.current = false;
      };
    } else if (!user?.id) {
      logger.warn('No user ID - skipping polling initialization', {
        hasUser: !!user,
        timestamp: new Date().toISOString()
      });
      
      // Clean up if user becomes null
      if (cleanupRef.current) {
        logger.debug('🧹 CLEANING UP POLLING SERVICE DUE TO NO USER');
        cleanupRef.current();
        cleanupRef.current = null;
      }
      // Reset subscription state
      subscriptionRef.current = false;
    }
  }, [user?.id]); // Only depend on user ID
  
  // Track unread accumulation metrics periodically
  // Disabled temporarily to fix infinite loop issue
  // TODO: Re-enable with proper dependency management
  /*
  useEffect(() => {
    if (!user?.id) return;
    
    const trackUnreadMetrics = () => {
      const allNotifications = [...bellNotifications, ...bannerNotifications];
      const unreadNotifications = allNotifications.filter(n => !n.read_at && !n.dismissed_at);
      
      // Calculate oldest unread age
      let oldestUnreadAge: number | undefined;
      if (unreadNotifications.length > 0) {
        const oldestUnread = unreadNotifications.reduce((oldest, current) => {
          const oldestTime = new Date(oldest.created_at).getTime();
          const currentTime = new Date(current.created_at).getTime();
          return currentTime < oldestTime ? current : oldest;
        });
        
        oldestUnreadAge = Date.now() - new Date(oldestUnread.created_at).getTime();
      }
      
      // Track unread accumulation by category
      const unreadByCategory: Record<string, number> = {};
      unreadNotifications.forEach(n => {
        const category = n.category || 'uncategorized';
        unreadByCategory[category] = (unreadByCategory[category] || 0) + 1;
      });
      
      analytics.trackUnreadAccumulation(unreadNotifications.length, {
        by_category: unreadByCategory,
        oldest_unread_age_ms: oldestUnreadAge
      });
    };
    
    // Track immediately and then every 5 minutes
    trackUnreadMetrics();
    const interval = setInterval(trackUnreadMetrics, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [user?.id]); // Only depend on user ID to prevent infinite loops
  */

  const markAsRead = useCallback(async (notificationId: string) => {
    if (!user?.id) return;
    
    try {
      await notificationService.markAsRead(notificationId, user.id);
      
      const readAt = new Date().toISOString();
      
      // Find the notification to track analytics
      const notification = [...bellNotifications, ...bannerNotifications].find(n => n.id === notificationId);
      
      if (notification) {
        const notificationAge = notification.created_at ? 
          Date.now() - new Date(notification.created_at).getTime() : undefined;
        
        // Defer analytics tracking to avoid blocking the UI
        setTimeout(() => {
          analytics.trackNotificationRead({
            notification_id: notificationId,
            notification_type: notification.notification_type,
            delivery_method: notification.delivery_method,
            category: notification.category,
            template_key: notification.template?.key,
            priority: notification.priority,
            time_to_interaction_ms: notificationAge,
            notification_age_ms: notificationAge,
            interaction_context: notification.delivery_method === 'bell' ? 'panel' : 'banner',
            read_method: 'manual'
          });
        }, 0);
      }
      
      // Update local state with batched updates
      setBellNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read_at: readAt } : n)
      );
      
      setBannerNotifications(prev => {
        const updated = prev.map(n => n.id === notificationId ? { ...n, read_at: readAt } : n);
        
        // Update active banner if it was marked as read
        setActiveBanner(current => {
          if (current?.id === notificationId) {
            // Find next unread banner from updated list
            const nextBanner = updated
              .filter(n => n.id !== notificationId && !n.read_at && !n.dismissed_at)
              .sort((a, b) => b.priority - a.priority)[0] || null;
            return nextBanner;
          }
          return current;
        });
        
        return updated;
      });
      
    } catch (err) {
      logger.error('Error marking notification as read:', err);
      setError('Failed to mark notification as read');
    }
  }, [user?.id, bellNotifications, bannerNotifications, analytics]);

  const dismissNotification = useCallback(async (notificationId: string): Promise<boolean> => {
    if (!user?.id) {
      logger.warn('NotificationContext: No user ID available for dismiss operation');
      return false;
    }
    
    try {
      logger.debug('NotificationContext: Starting dismiss operation:', { notificationId, userId: user.id });
      
      const success = await notificationService.dismissNotification(notificationId, user.id);
      
      if (!success) {
        logger.error('NotificationContext: Dismiss operation failed');
        setError('Failed to dismiss notification');
        return false;
      }
      
      logger.debug('NotificationContext: Dismiss operation succeeded');
      
      // Find the notification to track analytics before removing it
      const notification = [...bellNotifications, ...bannerNotifications].find(n => n.id === notificationId);
      
      if (notification) {
        const notificationAge = notification.created_at ? 
          Date.now() - new Date(notification.created_at).getTime() : undefined;
        
        // Defer analytics tracking to avoid blocking the UI
        setTimeout(() => {
          analytics.trackNotificationDismissed({
            notification_id: notificationId,
            notification_type: notification.notification_type,
            delivery_method: notification.delivery_method,
            category: notification.category,
            template_key: notification.template?.key,
            priority: notification.priority,
            time_to_interaction_ms: notificationAge,
            notification_age_ms: notificationAge,
            interaction_context: notification.delivery_method === 'bell' ? 'panel' : 'banner'
          });
        }, 0);
      }
      
      // Optimistically remove the notification from local state immediately
      // This provides instant feedback to the user
      setBellNotifications(prev => {
        const filtered = prev.filter(n => n.id !== notificationId);
        if (filtered.length !== prev.length) {
          logger.debug('Optimistically removed notification from bell notifications', { id: notificationId });
        }
        return filtered;
      });
      
      setBannerNotifications(prev => {
        const filtered = prev.filter(n => n.id !== notificationId);
        if (filtered.length !== prev.length) {
          logger.debug('Optimistically removed notification from banner notifications', { id: notificationId });
        }
        return filtered;
      });
      
      // Remove active banner if it's the one being dismissed
      setActiveBanner(current => {
        if (current?.id === notificationId) {
          logger.debug('Removed dismissed notification from active banner', { id: notificationId });
          // Find next unread banner
          const nextBanner = bannerNotifications
            .filter(n => n.id !== notificationId && !n.read_at && !n.dismissed_at)
            .sort((a, b) => b.priority - a.priority)[0] || null;
          return nextBanner;
        }
        return current;
      });
      
      return true;
    } catch (err) {
      logger.error('Error dismissing notification:', err);
      setError('Failed to dismiss notification');
      return false;
    }
  }, [user?.id, bannerNotifications, bellNotifications, analytics]);

  const markAllAsRead = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      // Mark all bell notifications as read
      const unreadBellIds = bellNotifications
        .filter(n => !n.read_at)
        .map(n => n.id);
      
      if (unreadBellIds.length === 0) return;
      
      await Promise.all(
        unreadBellIds.map(id => notificationService.markAsRead(id, user.id))
      );
      
      // Update local state
      const readAt = new Date().toISOString();
      setBellNotifications(prev => 
        prev.map(n => ({ ...n, read_at: readAt }))
      );
      
    } catch (err) {
      logger.error('Error marking all notifications as read:', err);
      setError('Failed to mark all notifications as read');
    }
  }, [user?.id, bellNotifications]);

  // Memoized unread count to prevent recalculation on every render
  const unreadCount = useMemo(() => {
    const count = bellNotifications.filter(n => !n.read_at).length;
    logger.debug('🔢 UNREAD COUNT RECALCULATED', { 
      bellNotificationsCount: bellNotifications.length,
      unreadCount: count,
      notifications: bellNotifications.map(n => ({
        id: n.id,
        title: n.title,
        read_at: !!n.read_at,
        dismissed_at: !!n.dismissed_at
      })),
      timestamp: new Date().toISOString()
    });
    return count;
  }, [bellNotifications]);

  // Get unread count for a specific page based on banner notifications
  const getUnreadCountForPage = useCallback((page: string): number => {
    const count = bannerNotifications.filter(notification => {
      // Check if this banner notification is for the specified page
      const targetPage = notification.metadata?.target_page;
      return targetPage === page && !notification.read_at && !notification.dismissed_at;
    }).length;
    
    logger.debug('Getting unread count for page', { 
      page, 
      count, 
      totalBannerNotifications: bannerNotifications.length 
    });
    return count;
  }, [bannerNotifications]);

  // Get total unread count (bell + banner) for mobile burger menu
  const getTotalUnreadCount = useCallback((): number => {
    const bellCount = unreadCount;
    const bannerCount = bannerNotifications.filter(n => !n.read_at && !n.dismissed_at).length;
    const total = bellCount + bannerCount;
    
    logger.debug('Getting total unread count', { 
      bellCount, 
      bannerCount, 
      total 
    });
    return total;
  }, [unreadCount, bannerNotifications]);

  // Get banner notifications for a specific page
  const getBannerNotificationsForPage = useCallback((page: string): UserNotification[] => {
    const pageNotifications = bannerNotifications.filter(notification => {
      const targetPage = notification.metadata?.target_page;
      return targetPage === page;
    });
    
    logger.debug('Getting banner notifications for page', { 
      page, 
      count: pageNotifications.length,
      notifications: pageNotifications.map(n => ({ id: n.id, read: !!n.read_at, dismissed: !!n.dismissed_at }))
    });
    return pageNotifications;
  }, [bannerNotifications]);

  // Auto-mark banner notifications as read when landing on a page
  const autoMarkBannersAsReadForPage = useCallback(async (page: string): Promise<void> => {
    const pageNotifications = getBannerNotificationsForPage(page);
    const unreadNotifications = pageNotifications.filter(n => !n.read_at && !n.dismissed_at);
    
    logger.debug('Auto-marking banners as read for page', { 
      page, 
      totalPageNotifications: pageNotifications.length,
      unreadCount: unreadNotifications.length 
    });

    // If there's exactly 1 unread banner notification for this page, auto-mark it as read
    if (unreadNotifications.length === 1) {
      const notification = unreadNotifications[0];
      logger.debug('Auto-marking single banner as read', { 
        page, 
        notificationId: notification.id,
        title: notification.title 
      });
      
      try {
        // Track auto-read analytics before marking as read
        const notificationAge = notification.created_at ? 
          Date.now() - new Date(notification.created_at).getTime() : undefined;
        
        // Defer analytics tracking to avoid blocking the UI
        setTimeout(() => {
          analytics.trackNotificationRead({
            notification_id: notification.id,
            notification_type: notification.notification_type,
            delivery_method: 'banner',
            category: notification.category,
            template_key: notification.template?.key,
            priority: notification.priority,
            time_to_interaction_ms: notificationAge,
            notification_age_ms: notificationAge,
            interaction_context: 'banner',
            read_method: 'auto'
          });
        }, 0);
        
        await markAsRead(notification.id);
        logger.debug('Successfully auto-marked banner as read', { 
          page, 
          notificationId: notification.id 
        });
      } catch (error) {
        logger.error('Failed to auto-mark banner as read', { 
          page, 
          notificationId: notification.id, 
          error 
        });
      }
    } else if (unreadNotifications.length > 1) {
      logger.debug('Multiple unread banners for page - user must interact to mark as read', { 
        page, 
        count: unreadNotifications.length 
      });
    }
  }, [getBannerNotificationsForPage, markAsRead, analytics]);

  // Memoized context value to prevent unnecessary re-renders
  const value: NotificationContextType = useMemo(() => ({
    bellNotifications,
    unreadCount,
    bannerNotifications,
    activeBanner,
    getUnreadCountForPage,
    getTotalUnreadCount,
    autoMarkBannersAsReadForPage,
    getBannerNotificationsForPage,
    markAsRead,
    dismissNotification,
    markAllAsRead,
    refreshNotifications,
    isLoading,
    error
  }), [
    bellNotifications,
    unreadCount,
    bannerNotifications,
    activeBanner,
    getUnreadCountForPage,
    getTotalUnreadCount,
    autoMarkBannersAsReadForPage,
    getBannerNotificationsForPage,
    markAsRead,
    dismissNotification,
    markAllAsRead,
    refreshNotifications,
    isLoading,
    error
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}; 